import React, { useMemo, useState, useRef } from 'react';
import PropTypes, { any } from 'prop-types';
import { Form } from 'react-form';
import { useSelector } from 'react-redux';
import moment from 'moment-timezone';

import Popup from '../../Popup';
import { CheckBox, DateTimePicker, Input, Select, ToggleSwitch } from '../../Form';
import { required } from '../../../utils/validation';
import {
    getAllowedFormatTypes,
    getDefaultEliminationType,
    isPickleBallSportTypeEvent,
    updateEventDrawScoringTypeOverrides,
} from '../../../utils/events';
import {
    ballTypeIds,
    ballTypes,
    divisionFormatTypeIds,
    drawCategoryTypeIds,
    drawEliminationTypeIds,
    eventGenderTypeIds,
    feedInBackDrawFormatOptions,
    officialsTypeIds,
} from '../../../utils/eventConstants';
import ScoringTypeAllottedTimeField from './ScoringTypeAllottedTimeField';
import GroupDrawFormatFields from './GroupDrawFormatFields';
import { allowedMatchTimes } from '../../../utils/allottedTimeConstants';
import useDrawEdit from './useDrawEdit';
import { shouldDrawNameFieldBeDisabled } from './utils';
import { getDivisionsOfPlayersAndTeamsLinkedToDraw, getPlayersOrTeamsCountPlacedInDraw } from '../../../utils/draws';

import SplashV3, { SplashScope } from '../../Splash/splashV3';
import QualifyingDrawSection from './QualifyingDrawSection';
import NonGroupDrawFormatFields from './NonGroupDrawFormatFields';
import { sortObjectsByNameAsc } from '../../../utils/sorting';
import { useStatsig } from '../../hooks/useStatsig';
import useCloseAnimation from '../../hooks/useCloseAnimation';

import styles from './eventDrawCreateEditPopup.scss';
import links from '../../../../config/links';

function EventDrawCreateEditPopup(props) {
    const {
        submitting,
        onCloseClick,
        handleSubmit,
        forceDoubles,
        createEmptyDraw,
        sportTypeId,
        eventTypeId,
        isVerifiedEvent,
        timeZone,
        eventSchedule,
        isInEditMode,
        drawToEdit,
        parentDraw,
        eventId,
        customSubmitText,
        customCancelText,
        eventAllottedMatchTimes,
        divisions,
        drawDivision,
        associatedDivisionIds,
        associatedDivisionsByDrawId,
        playersDictByMemberId,
        teamsDict,
        divisionDrawAssociation,
        awardDrawMedal,
        drawsDict,
        allowMomentTimeZoneOverride,
    } = props;

    const coreUI = useSelector(({ coreUI }) => coreUI);
    const drawEdit = useSelector(({ drawEdit }) => drawEdit);
    const isIphone = /iPhone/i.test(navigator.userAgent);
    const {
        isAllottedTimeFlagEnabled,
        selectableDrawEliminationTypes,
        proDrawEliminationTypes,
        drawDataFromEditSpecificApi,
        dateToDisplay,
        showFicFormatTypeField,
        allowRoundResize,
        selectableDrawSizes,
        ficFormatTypes,
        isDrawSizeChanged,
        playOffDrawOptions,
        scoringFormatOverrideOptions,
        defaultEliminationTypeId,
        isDoubleEliminationDraw,
        onChangeScoringType,
        onFormChange,
        onFormatTypeIdChange,
        onGenderIdChange,
        onDrawEliminationChange,
        onDrawSizeChange,
        handleDateChange,
        onChangeFicFormatTypeId,
        onChangeFibFeedOutRound,
        getScoringFormats,
        onPlayOffDrawCheckChange,
        getDefaultValuesFromDrawSpecificData,
        onChangeAutoFillType,
        onGroupPlayersAdvancingChangeForGroupDraws,
        onGroupPlayersAdvancingChangeForNonGroupDraws,
        resetGroupPlayersAdvancingToPlayoffCount,
        onGroupsCountChange,
    } = useDrawEdit({
        sportTypeId,
        eventAllottedMatchTimes,
        isInEditMode,
        eventId,
        draw: drawToEdit,
        timeZone,
        isVerifiedEvent,
        eventTypeId,
        forceDoubles,
        allowMomentTimeZoneOverride,
    });

    const {
        genderTypes,
    } = coreUI.types.tms;

    const qualifyingDrawRef = useRef();
    const preQualifyingDrawRef = useRef();

    const { checkGate, featureGates } = useStatsig();
    const [showDivisionError, setShowDivisionError] = useState(false);

    const { selected, selectedTeams, players, teams } = drawEdit;
    const selectedPlayersDivisionIds = [];
    const selectedTeamsDivisionIds = [];
    selected.forEach(memberId => {
        const playerDivisions = playersDictByMemberId[memberId]?.divisions;
        if (playerDivisions?.length) {
            playerDivisions.forEach(div => {
                if (!selectedPlayersDivisionIds.includes(div.id)) {
                    selectedPlayersDivisionIds.push(div.id);
                }
            });
        }
    });
    selectedTeams.forEach(teamId => {
        const teamDivisions = teamsDict[teamId]?.divisions;
        if (teamDivisions?.length) {
            teamDivisions.forEach(div => {
                if (!selectedTeamsDivisionIds.includes(div.id)) {
                    selectedTeamsDivisionIds.push(div.id);
                }
            });
        }
    });
    const drawPlayersDivisionIds = useMemo(() => {
        if (isInEditMode) {
            return getDivisionsOfPlayersAndTeamsLinkedToDraw(
                drawToEdit,
                drawsDict,
                players?.data,
                teams?.data,
            );
        }

        return [];
    }, [isInEditMode, drawToEdit, drawsDict, players?.data, teams?.data]);

    const selectedCount = useMemo(() => {
        if (isInEditMode) {
            return getPlayersOrTeamsCountPlacedInDraw(
                drawToEdit,
                players?.data,
                teams?.data,
            );
        }

        return 0;
    }, [isInEditMode, drawToEdit, players?.data, teams?.data]);

    const eventStartDate = moment.utc(eventSchedule.eventStartUtc).tz(timeZone.value);
    const eventEndDate = moment.utc(eventSchedule.eventEndUtc).tz(timeZone.value);

    const isTimeBasedFormatEnabled = checkGate(featureGates.WEB_TIME_BASED_SETTINGS) && !isPickleBallSportTypeEvent(sportTypeId);
    const isDinglesEnabled = checkGate(featureGates.DINGLES);
    const formatTypes = getAllowedFormatTypes({
        coreUI,
        isDinglesEnabled,
    });

    const showBallTypeField = !isPickleBallSportTypeEvent(sportTypeId);

    const hasGroupDrawParent = parentDraw?.categoryTypeId === drawCategoryTypeIds.GROUPED;

    const isDrawDivisionAssociationEnabled = checkGate(featureGates.DRAW_DIVISION_ASSOCIATION) && divisionDrawAssociation;
    const isAwardingMedalEnabled = awardDrawMedal;

    const hasSelectedDivision = (values) => {
        // eslint-disable-next-line no-unused-vars
        return Object.entries(values.divisionIds || {}).length ? !!(Object.entries(values.divisionIds).filter(([key, val]) => val).length) : false;
    };

    const onSubmit = (values) => {
        const {
            areOfficialsAllowed,
            ballTypeId,
            qualifyingDraw,
            preQualifyingDraw,
            ...otherValues
        } = values;

        if (otherValues.formatTypeId === divisionFormatTypeIds.DINGLES) {
            otherValues.isRated = false;
        }

        if (isDrawDivisionAssociationEnabled && !hasSelectedDivision(values)) {
            setShowDivisionError(true);
            return;
        }

        // eslint-disable-next-line no-unused-vars
        const divisionIds = Object.entries(values.divisionIds || {}).length ? Object.entries(values.divisionIds).filter(([key, val]) => val).map(([key, val]) => key.slice(1)) : [];

        const updatedValues = updateEventDrawScoringTypeOverrides({
            ...otherValues,
            officialsTypeId: areOfficialsAllowed ? officialsTypeIds.YES : officialsTypeIds.NO,
            ballTypeId: parseInt(ballTypeId) !== parseInt(ballTypeIds.YELLOW) ? ballTypeId : null,
            // eslint-disable-next-line no-unused-vars
            divisionIds,
            ...(qualifyingDraw?.enabled ? { qualifyingDraw } : {}),
            ...(preQualifyingDraw?.enabled ? { preQualifyingDraw } : {}),
        });

        handleSubmit({ values: updatedValues, createEmptyDraw });
    };

    const onKeyPress = (event) => {
        if (event.key === 'Enter' && event.target.type !== 'submit' && event.target.id !== 'details') {
            event.preventDefault();
        }
    };

    const onEventCreateEditFormChange = (formState) => {
        onFormChange(formState);
        if (qualifyingDrawRef.current) {
            qualifyingDrawRef.current.onFormValuesChanged(formState);
        }
        if (preQualifyingDrawRef.current) {
            preQualifyingDrawRef.current.onFormValuesChanged(formState);
        }
    };

    const shouldDisplayForm = isInEditMode ? drawDataFromEditSpecificApi : true;
    const [isVisible, setIsVisible] = useState(true);
    const { isClosing } = useCloseAnimation({ isVisible, onClose: onCloseClick });
    const defaultValues = useMemo(() => {
        if (isInEditMode && drawDataFromEditSpecificApi) {
            const { hasScheduledMatches, ...others } = drawDataFromEditSpecificApi;
            return getDefaultValuesFromDrawSpecificData(
                others,
                parentDraw,
                drawDivision,
            );
        }

        const defaultElimTypeId = getDefaultEliminationType(eventTypeId, sportTypeId);
        return {
            name: null,
            formatTypeId: forceDoubles ? divisionFormatTypeIds.DOUBLES : divisionFormatTypeIds.SINGLES,
            genderId: eventGenderTypeIds.ALL,
            ballTypeId: ballTypeIds.YELLOW,
            areOfficialsAllowed: false,
            isRated: true,
            isQualifier: false,
            eliminationTypeId: defaultElimTypeId,
            drawSize: selectableDrawSizes && selectableDrawSizes.length > 0 ? selectableDrawSizes[0].id : null,
            roundSize: 1,
            matchesPerRound: 1,
            startDateTimeUtc: null,
            notAllowMovingWinnersToMainFromBackDraw: false,
            awardMedal: true,
            isTimeBased: false,
        };
    }, [forceDoubles, defaultEliminationTypeId, selectableDrawSizes, isInEditMode, drawDataFromEditSpecificApi, drawToEdit, parentDraw]);

    const getFilteredDivisions = (values) => {
        let filteredDivisions = divisions.filter(div => div.divisionFormatTypeId === parseInt(values.formatTypeId) && div.genderId === parseInt(values.genderId)).sort(sortObjectsByNameAsc);
        if (!isInEditMode && selected.length) {
            filteredDivisions = filteredDivisions.filter(div => selectedPlayersDivisionIds.includes(div.id));
        }
        if (!isInEditMode && selectedTeams.length) {
            filteredDivisions = filteredDivisions.filter(div => selectedTeamsDivisionIds.includes(div.id));
        }
        return filteredDivisions;
    };

    const isDivisionAssociatedToDraw = (divId) => {
        if (hasGroupDrawParent) {
            return associatedDivisionsByDrawId[parentDraw?.id]?.includes(divId);
        }

        return associatedDivisionsByDrawId[drawToEdit.id]?.includes(divId);
    };

    const shouldDivisionBeDisabled = (divId) => {
        // if a division has been associated with a draw, we should disable (they can only be associated to one draw)
        // unless the given draw has no players, then we should allow them to unselect existing division associations
        if (isInEditMode &&
            !drawDataFromEditSpecificApi.hasPostedResults &&
            !drawPlayersDivisionIds.includes(divId) &&
            isDivisionAssociatedToDraw(divId)) {
            return false;
        }

        return associatedDivisionIds.includes(divId);
    };

    const submitText = useMemo(() => {
        if (customSubmitText) {
            return customSubmitText;
        }

        return isInEditMode ? 'Update' : 'Create';
    }, [customSubmitText, isInEditMode]);

    const cancelText = customCancelText || 'Cancel';

    const handleClose = () => {
        if (!submitting) {
            setIsVisible(false);
        }
    };

    const validateDivisionDate = (value) => {
        if (value && moment.utc(value)?.isBefore(eventStartDate)) {
            return {
                error: 'Date can\'t be before the event start date and time.',
            };
        }
        if (value && moment.utc(value)?.isAfter(eventEndDate)) {
            return {
                error: 'Date can\'t be after the event end date and time.',
            };
        }
        return null;
    };

    return (
        <Popup
            className="popup-sm"
            popUpClassName={styles.eventDrawCreateEditPopup}
            data-testid="draw-edit.create-edit-popup"
            onCloseClick={handleClose}
            config={{
                fullScreen: false,
                noPadding: true,
                scrollable: false,
                showCloseIcon: true,
                title: isInEditMode ? 'Edit draw' : 'Create draw',
                isSideBar: true,
                isClosing,
            }}
        >
            {shouldDisplayForm && (
                <Form onSubmit={onSubmit} defaultValues={defaultValues} onChange={onEventCreateEditFormChange}>
                    {(formApi) => {
                        const { submitForm, values } = formApi;

                        return (
                            <form onSubmit={submitForm} id="drawEditForm" onKeyPress={onKeyPress}>
                                <div className={styles.content}>
                                    <div className={styles.section}>
                                        {isInEditMode && values.categoryTypeId === drawCategoryTypeIds.GROUPED && (
                                            <p className={`${styles.description} text-error`}>
                                                Any edits you make will update all group/playoff draws for this draw.
                                            </p>
                                        )}
                                        <h6 className={styles.title}>
                                            Draw Info
                                        </h6>
                                        {hasGroupDrawParent && (
                                            <Input
                                                label="Draw name"
                                                id="groupName"
                                                field="groupName"
                                                placeholder="Ex. Men's Singles"
                                                validate={required}
                                                themeV3
                                                data-testid="draw-create.group-name-input"
                                            />
                                        )}
                                        <Input
                                            label={hasGroupDrawParent ? 'Group name' : 'Draw name'}
                                            id="name"
                                            field="name"
                                            placeholder="Ex. Men's Singles"
                                            validate={required}
                                            themeV3
                                            disabled={shouldDrawNameFieldBeDisabled(isInEditMode, values, parentDraw)}
                                            data-testid="draw-create.name-input"
                                        />
                                        <DateTimePicker
                                            id="tourDateStart"
                                            field="startDateTimeUtc"
                                            data-testid="draw-create.start-date-picker"
                                            dateLabel="Start date"
                                            timeLabel="Start time"
                                            selected={dateToDisplay}
                                            minDate={eventStartDate}
                                            maxDate={eventEndDate}
                                            handleDateChange={handleDateChange('startDateTimeUtc', formApi)}
                                            themeV3
                                            defaultDate={eventStartDate}
                                            validate={value => validateDivisionDate(value)}
                                        />
                                        <Select
                                            id="formatTypeId"
                                            label="Game type"
                                            field="formatTypeId"
                                            options={formatTypes}
                                            validate={required}
                                            disabled={forceDoubles || isInEditMode}
                                            onChange={onFormatTypeIdChange(formApi)}
                                            themeV3
                                            data-testid="draw-create.format-type-select"
                                        />
                                        <Select
                                            id="genderId"
                                            label="Gender"
                                            field="genderId"
                                            options={genderTypes}
                                            validate={required}
                                            onChange={onGenderIdChange(formApi)}
                                            themeV3
                                            data-testid="draw-create.gender-select"
                                        />
                                        {showBallTypeField && (
                                            <Select
                                                id="ballTypeId"
                                                label="Ball type"
                                                field="ballTypeId"
                                                options={ballTypes}
                                                validate={required}
                                                themeV3
                                                data-testid="draw-create.ball-type-select"
                                            />
                                        )}
                                        <CheckBox
                                            id="areOfficialsAllowed"
                                            formGroupCssClass={`mb10 ${styles.dummy}`}
                                            customLabel="This event will have on-site officials"
                                            field="areOfficialsAllowed"
                                            data-testid="draw-create.officials-checkbox"
                                        />
                                        <CheckBox
                                            id="isRated"
                                            formGroupCssClass={`mb10 ${styles.dummy}`}
                                            customLabel="Results will count towards ratings"
                                            field="isRated"
                                            data-testid="draw-create.is-rated-checkbox"
                                        />
                                        {parseInt(values.eliminationTypeId) !== drawEliminationTypeIds.PRO_DRAWS && (
                                            <CheckBox
                                                id="isQualifier"
                                                formGroupCssClass={`mb10 ${styles.dummy}`}
                                                customLabel="Mark as qualifying draw"
                                                field="isQualifier"
                                                data-testid="draw-create.is-qualifier-checkbox"
                                            />
                                        )}
                                        {isAwardingMedalEnabled && (
                                            parseInt(values.eliminationTypeId) === drawEliminationTypeIds.SINGLE_ELIMINATION ||
                                            parseInt(values.eliminationTypeId) === drawEliminationTypeIds.DOUBLE_ELIMINATION ||
                                            parseInt(values.eliminationTypeId) === drawEliminationTypeIds.AD_HOC ||
                                            parseInt(values.eliminationTypeId) === drawEliminationTypeIds.ROUND_ROBIN ||
                                            parseInt(values.eliminationTypeId) === drawEliminationTypeIds.PRO_DRAWS
                                        ) && (
                                            <CheckBox
                                                id="awardMedal"
                                                formGroupCssClass={`mb10 ${styles.dummy}`}
                                                customLabel="This draw will award medals"
                                                field="awardMedal"
                                                selected={selected}
                                                data-testid="draw-create.award-medal-checkbox"
                                            />
                                        )}
                                    </div>
                                    {isDrawDivisionAssociationEnabled && (
                                        <div className={styles.section}>
                                            <h6 className={styles.title}>
                                                Associated division(s)
                                            </h6>
                                            {showDivisionError && <div className="text-small text-error" data-testid="draw-edit.create-edit-popup.division-error-msg">You must select at least one division</div>}
                                            {getFilteredDivisions(values).map(div => (
                                                <CheckBox
                                                    key={`division${div.id}`}
                                                    id={`division${div.id}`}
                                                    customLabel={div.name}
                                                    field={`divisionIds[_${div.id}]`}
                                                    disabled={shouldDivisionBeDisabled(div.id)}
                                                    data-testid={`draw-create.division-${div.id}-checkbox`}
                                                />
                                            ))}
                                            {!getFilteredDivisions(values).length && (
                                                <div className="text-small">There are no divisions which can be associated with this draw&apos;s game type and gender</div>
                                            )}
                                        </div>
                                    )}
                                    <div className={styles.section}>
                                        <h6 className={styles.title}>
                                            Draw Format
                                        </h6>
                                        <Select
                                            id="eliminationTypeId"
                                            label="Draw type"
                                            field="eliminationTypeId"
                                            options={selectableDrawEliminationTypes}
                                            validate={required}
                                            onChange={onDrawEliminationChange(formApi, false)}
                                            disabled={isInEditMode}
                                            themeV3
                                            data-testid="draw-create.elimination-type-select"
                                        />
                                        {parseInt(values.categoryTypeId) !== drawCategoryTypeIds.GROUPED && (
                                            <NonGroupDrawFormatFields
                                                values={values}
                                                isInEditMode={isInEditMode}
                                                drawToEdit={drawToEdit}
                                                parentDraw={parentDraw}
                                                proDrawEliminationTypes={proDrawEliminationTypes}
                                                onDrawEliminationChange={onDrawEliminationChange(formApi, true)}
                                                allowRoundResize={allowRoundResize}
                                                selectableDrawSizes={selectableDrawSizes}
                                                onDrawSizeChange={onDrawSizeChange(formApi)}
                                                ficFormatTypes={ficFormatTypes}
                                                onChangeFicFormatTypeId={onChangeFicFormatTypeId(formApi)}
                                                showFicFormatTypeField={showFicFormatTypeField}
                                                isDrawSizeChanged={isDrawSizeChanged}
                                                feedInBackDrawFormatOptions={feedInBackDrawFormatOptions}
                                                onChangeFibFeedOutRound={onChangeFibFeedOutRound(formApi)}
                                                isDoubleEliminationDraw={isDoubleEliminationDraw}
                                                playOffDrawOptions={playOffDrawOptions}
                                                onPlayOffDrawCheckChange={onPlayOffDrawCheckChange(formApi)}
                                                drawDataFromEditSpecificApi={drawDataFromEditSpecificApi}
                                                onGroupPlayersAdvancingChange={onGroupPlayersAdvancingChangeForNonGroupDraws(formApi)}
                                                resetGroupPlayersAdvancingToPlayoffCount={resetGroupPlayersAdvancingToPlayoffCount(formApi)}
                                            />
                                        )}
                                        {parseInt(values.categoryTypeId) === drawCategoryTypeIds.GROUPED && (
                                            <GroupDrawFormatFields
                                                values={values}
                                                isInEditMode={isInEditMode}
                                                onChangeAutoFillType={onChangeAutoFillType(formApi)}
                                                selectedCount={selectedCount}
                                                onGroupsCountChange={onGroupsCountChange(formApi)}
                                                playOffDrawOptions={playOffDrawOptions}
                                                onPlayOffDrawCheckChange={onPlayOffDrawCheckChange(formApi)}
                                                onGroupPlayersAdvancingChange={onGroupPlayersAdvancingChangeForGroupDraws(formApi)}
                                                drawDataFromEditSpecificApi={drawDataFromEditSpecificApi}
                                                resetGroupPlayersAdvancingToPlayoffCount={resetGroupPlayersAdvancingToPlayoffCount(formApi)}
                                            />
                                        )}
                                        {isInEditMode && isDrawSizeChanged && (
                                            <div className="form-block-content pt8">
                                                <div className={`${styles.title} mb4`}>
                                                    You have changed the size of the draw, please read carefully:
                                                </div>
                                                <ul className={`${styles.description} mb0`}>
                                                    <li>Changing the size of a draw resets all current draw data.</li>
                                                    <li>All currently assigned positions will be reset and all entered results will be deleted.</li>
                                                    {(values.eliminationTypeId === drawEliminationTypeIds.COMPASS || values.eliminationTypeId === drawEliminationTypeIds.FIRST_MATCH_BACKDRAW) && (
                                                        <>
                                                            <li>For a main draw, its back draw will also be resized and all data reset.</li>
                                                            <li>For back draws, they will not alter the main draw.</li>
                                                        </>
                                                    )}
                                                </ul>
                                            </div>
                                        )}
                                    </div>
                                    <div className={styles.section}>
                                        <div>
                                            <div className={`d-flex aic justify-content-space-between`}>
                                                <h6 className={`${styles.title} mr-auto`}>
                                                    Scoring Formats
                                                </h6>
                                                {(isTimeBasedFormatEnabled || values.isTimeBased) && (
                                                    <ToggleSwitch
                                                        id="isTimeBased"
                                                        field="isTimeBased"
                                                        label="Time Based"
                                                        formGroupCssClass={`mb0 ${styles.timeBasedSwitch}`}
                                                        reverse={false}
                                                        disabled={isInEditMode && hasGroupDrawParent}
                                                        data-testid="draw-create.time-based-toggle"
                                                        hoverText={(
                                                            <>
                                                                <div className="fw600 fs16 mb8">
                                                                    Time Based Match Scoring Format
                                                                </div>
                                                                <ul>
                                                                    <li>
                                                                        A timed-based match concludes when the allotted time period is met, regardless of the scoring format or current score.
                                                                    </li>
                                                                    <li>
                                                                        Timed-based matches are required to have a minimum duration of 30 minutes.
                                                                    </li>
                                                                    <li>
                                                                        To count toward a player&apos;s Verified UTR rating, a minimum of 4 games must be won by one player or team.
                                                                    </li>
                                                                </ul>
                                                                <div>
                                                                    For more information on Timed-Based match rules, please  <a href={links.verifiedRules} target="_blank" rel="noopener noreferrer">click here</a>.
                                                                </div>
                                                            </>
                                                        )}
                                                    />
                                                )}
                                            </div>
                                            {values.isTimeBased && (
                                                <div className={`mt8 fs14 fw400`}>
                                                    By enabling Timed-Based Scoring, you confirm that all matches in this division will last at least 30 minutes and follow the <a href={links.verifiedRules} target="_blank" rel="noopener noreferrer">UTR timed-based scoring rules</a>.
                                                </div>
                                            )}
                                        </div>
                                        {!isAllottedTimeFlagEnabled && (
                                            <Select
                                                id="scoringTypeId"
                                                label="Default scoring format"
                                                field="scoringTypeId"
                                                options={getScoringFormats(values)}
                                                themeV3
                                                data-testid="draw-create.scoring-type-select"
                                            />
                                        )}
                                        {isAllottedTimeFlagEnabled && (
                                            <div className="d-flex align-items-start">
                                                <div className="flex-1">
                                                    <Select
                                                        id="scoringTypeId"
                                                        label="Default scoring format"
                                                        field="scoringTypeId"
                                                        options={getScoringFormats(values)}
                                                        themeV3
                                                        formGroupCssClass={`mb0`}
                                                        validate={required}
                                                        onChange={onChangeScoringType(formApi)}
                                                        data-testid="draw-create.scoring-type-select"
                                                    />
                                                </div>
                                                <div className="flex-1 ml16">
                                                    <Select
                                                        id="allottedMatchTime"
                                                        field="allottedMatchTime"
                                                        label="Default allotted time"
                                                        options={allowedMatchTimes}
                                                        themeV3
                                                        formGroupCssClass={`mb0`}
                                                        validate={required}
                                                        data-testid="draw-create.allotted-time-select"
                                                        disabled={drawDataFromEditSpecificApi?.hasScheduledMatches}
                                                    />
                                                </div>
                                            </div>
                                        )}
                                        {isAllottedTimeFlagEnabled && scoringFormatOverrideOptions.map((scoringFormatOverride) => {
                                            return (
                                                <ScoringTypeAllottedTimeField
                                                    key={`override-${scoringFormatOverride.id}`}
                                                    scoringFormatOverride={scoringFormatOverride}
                                                    scoringTypeOptions={getScoringFormats(values)}
                                                    formApi={formApi}
                                                    values={values}
                                                    eventAllottedMatchTimes={eventAllottedMatchTimes}
                                                    disableAllottedTimesField={drawDataFromEditSpecificApi?.hasScheduledMatches}
                                                />
                                            );
                                        })}
                                    </div>
                                    {parseInt(values.eliminationTypeId) === drawEliminationTypeIds.PRO_DRAWS && (
                                        <QualifyingDrawSection
                                            sportTypeId={sportTypeId}
                                            eventTypeId={eventTypeId}
                                            className={styles.section}
                                            field="qualifyingDraw"
                                            formApi={formApi}
                                            qualifyingDrawType="qualifying"
                                            isInEditMode={isInEditMode}
                                            forceDoubles={forceDoubles}
                                            isVerifiedEvent={isVerifiedEvent}
                                            eventAllottedMatchTimes={eventAllottedMatchTimes}
                                            ref={qualifyingDrawRef}
                                            drawDataFromEditSpecificApi={drawDataFromEditSpecificApi}
                                            eventSchedule={eventSchedule}
                                        />
                                    )}
                                    {parseInt(values.eliminationTypeId) === drawEliminationTypeIds.PRO_DRAWS && values.qualifyingDraw?.enabled && (
                                        <QualifyingDrawSection
                                            sportTypeId={sportTypeId}
                                            eventTypeId={eventTypeId}
                                            className={styles.section}
                                            field="preQualifyingDraw"
                                            formApi={formApi}
                                            qualifyingDrawType="pre-qualifying"
                                            isInEditMode={isInEditMode}
                                            forceDoubles={forceDoubles}
                                            isVerifiedEvent={isVerifiedEvent}
                                            eventAllottedMatchTimes={eventAllottedMatchTimes}
                                            ref={preQualifyingDrawRef}
                                            drawDataFromEditSpecificApi={drawDataFromEditSpecificApi}
                                            eventSchedule={eventSchedule}
                                        />
                                    )}
                                </div>
                                <div className={styles.footer} style={isIphone ? null : { position: 'fixed', width: '400px', bottom: '0px', right: '0px', backgroundColor: '#fffff', paddingLeft: '20px', paddingRight: '20px', zIndex: 999 }}>
                                    <button
                                        onClick={handleClose}
                                        type="button"
                                        className="btn btn-default text-uppercase flex-1"
                                        disabled={submitting}
                                        data-testid="draw-edit.create-edit-popup.cancel-btn"
                                    >
                                        {cancelText}
                                    </button>
                                    <button
                                        type="submit"
                                        className="btn btn-primary text-uppercase flex-1"
                                        disabled={submitting}
                                        data-testid="draw-edit.create-edit-popup.submit-btn"
                                    >
                                        {submitting && (<i className="fas fa-circle-notch fa-spin" />)} {submitText} DRAW
                                    </button>
                                </div>
                            </form>
                        );
                    }}
                </Form>
            )}
            {!shouldDisplayForm && (
                <SplashV3
                    scope={SplashScope.COMPONENT}
                />
            )}
        </Popup>
    );
}

EventDrawCreateEditPopup.propTypes = {
    submitting: PropTypes.bool,
    onCloseClick: PropTypes.func.isRequired,
    handleSubmit: PropTypes.func.isRequired,
    forceDoubles: PropTypes.bool,
    isVerifiedEvent: PropTypes.bool,
    createEmptyDraw: PropTypes.bool,
    isInEditMode: PropTypes.bool,
    sportTypeId: PropTypes.number.isRequired,
    eventTypeId: PropTypes.number.isRequired,
    eventSchedule: PropTypes.objectOf(any).isRequired,
    timeZone: PropTypes.objectOf(any).isRequired,
    drawToEdit: PropTypes.objectOf(any),
    parentDraw: PropTypes.objectOf(any),
    eventId: PropTypes.string,
    customSubmitText: PropTypes.string,
    customCancelText: PropTypes.string,
    eventAllottedMatchTimes: PropTypes.arrayOf(any),
    drawDivision: PropTypes.objectOf(any),
    associatedDivisionIds: PropTypes.arrayOf(any),
    associatedDivisionsByDrawId: PropTypes.objectOf(any),
    divisions: PropTypes.arrayOf(any),
    awardDrawMedal: PropTypes.bool,
    drawsDict: PropTypes.objectOf(any).isRequired,
    playersDictByMemberId: PropTypes.objectOf(any).isRequired,
    allowMomentTimeZoneOverride: PropTypes.bool,
};

EventDrawCreateEditPopup.defaultProps = {
    submitting: false,
    forceDoubles: false,
    isVerifiedEvent: false,
    createEmptyDraw: false,
    isInEditMode: false,
    drawToEdit: null,
    parentDraw: null,
    eventId: null,
    customSubmitText: null,
    customCancelText: null,
    eventAllottedMatchTimes: [],
    drawDivision: null,
    associatedDivisionIds: [],
    associatedDivisionsByDrawId: {},
    divisions: [],
    awardDrawMedal: false,
    allowMomentTimeZoneOverride: true,
};

export default EventDrawCreateEditPopup;
